'use client'

import {
    AssistantAppliedAction,
    resetAssistantConversation,
    sendAssistantMessage,
    undoAssistantAction,
} from '@/lib/api/catalog/catalog-assistant-api'
import { useAuth } from '@/store/hooks'
import { useEffect, useRef, useState } from 'react'
import { MdAutoAwesome, MdClose, MdRefresh, MdSend, MdUndo } from 'react-icons/md'
import { toast } from 'react-toastify'

interface ChatMessage {
    role: 'user' | 'assistant'
    content: string
    /** Escritas aplicadas junto desta resposta, com o botão de desfazer. */
    applied?: AssistantAppliedAction[]
}

interface CatalogAssistantPanelProps {
    open: boolean
    onClose: () => void
    /** Chamado quando o assistente grava algo, para a listagem recarregar. */
    onCatalogChanged: () => void
}

const SUGGESTIONS = [
    'Adiciona Tomografia de crânio ao Laboratório Vida',
    'Não estou encontrando Ultrassom de abdome total no catálogo',
    'O valor da consulta de cardiologia nesse parceiro está errado',
]

export function CatalogAssistantPanel({
    open,
    onClose,
    onCatalogChanged,
}: CatalogAssistantPanelProps) {
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [input, setInput] = useState('')
    const [loading, setLoading] = useState(false)
    const [conversationId, setConversationId] = useState<string | undefined>()
    const [undoing, setUndoing] = useState<string | null>(null)

    const bottomRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLTextAreaElement>(null)

    const { user } = useAuth()
    const companyId = user?.companyId

    /**
     * A conversa é retomada de onde parou, por usuário e por filial.
     *
     * Guardar só na memória do React perdia todo o contexto ao recarregar a página,
     * fechar o painel ou o navegador cair no meio de um cadastro. O servidor mantém
     * o histórico por 24h; aqui fica apenas o identificador, mais o que já foi
     * exibido, para a tela não voltar em branco.
     *
     * A chave inclui a filial porque o backend amarra a conversa a (usuário,
     * empresa): trocar de unidade abre outra conversa, e cada uma tem de lembrar a
     * sua. Sem isso, o painel mostrava o histórico da filial anterior enquanto o
     * assistente já respondia por outra.
     */
    const storageKey = `catalog-assistant:${user?.id ?? 'anon'}:${companyId ?? 'none'}`

    useEffect(() => {
        try {
            const saved = localStorage.getItem(storageKey)
            const parsed = saved ? JSON.parse(saved) : null
            setConversationId(parsed?.conversationId)
            setMessages(Array.isArray(parsed?.messages) ? parsed.messages : [])
        } catch {
            setConversationId(undefined)
            setMessages([])
        }
        setInput('')
    }, [storageKey])

    useEffect(() => {
        try {
            if (!conversationId && messages.length === 0) {
                localStorage.removeItem(storageKey)
                return
            }
            // Grava mesmo sem `conversationId`: o identificador só nasce quando a
            // primeira resposta volta, e se o navegador cair antes disso o operador
            // ao menos reencontra o que escreveu, em vez de uma tela em branco.
            localStorage.setItem(storageKey, JSON.stringify({ conversationId, messages }))
        } catch {
            // cota do localStorage estourada: a conversa continua viva no servidor,
            // só não é reexibida depois de recarregar
        }
    }, [storageKey, conversationId, messages])

    /** Reinício manual, para quando o agente não perceber que o assunto mudou. */
    async function handleReset() {
        const response = await resetAssistantConversation(conversationId)
        setConversationId(response.result?.conversationId)
        setMessages([])
        setInput('')
    }

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages, loading])

    useEffect(() => {
        if (open) inputRef.current?.focus()
    }, [open])

    async function handleSend(text?: string) {
        const message = (text ?? input).trim()
        if (!message || loading) return

        setInput('')
        setMessages(current => [...current, { role: 'user', content: message }])
        setLoading(true)

        try {
            const response = await sendAssistantMessage({ message, conversationId })
            const data = response.result

            if (!data) {
                toast.error(response.error || 'Não consegui falar com o assistente')
                return
            }

            setConversationId(data.conversationId)

            if (data.conversationReset) {
                // O agente encerrou o assunto. A resposta dele já explica o que está
                // sendo esquecido, então ela fica visível sozinha na tela limpa —
                // manter o histórico anterior contradiria o que ele acabou de dizer.
                setMessages([{ role: 'assistant', content: data.reply, applied: data.applied }])
            } else {
                setMessages(current => [
                    ...current,
                    { role: 'assistant', content: data.reply, applied: data.applied },
                ])
            }

            if (data.applied?.length) onCatalogChanged()
        } catch {
            toast.error('Erro ao falar com o assistente')
        } finally {
            setLoading(false)
        }
    }

    async function handleUndo(action: AssistantAppliedAction) {
        setUndoing(action.logId)
        try {
            const response = await undoAssistantAction(action.logId)
            if (response.result?.success) {
                toast.success('Ação desfeita')
                setMessages(current =>
                    current.map(item => ({
                        ...item,
                        applied: item.applied?.filter(entry => entry.logId !== action.logId),
                    }))
                )
                onCatalogChanged()
            } else {
                toast.error(response.error || 'Não consegui desfazer')
            }
        } catch {
            toast.error('Erro ao desfazer a ação')
        } finally {
            setUndoing(null)
        }
    }

    if (!open) return null

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div
                className="absolute inset-0 bg-black/40"
                onClick={onClose}
                aria-hidden="true"
            />

            <aside className="relative flex h-full w-full max-w-lg flex-col bg-base-100 shadow-2xl">
                <header className="flex items-center justify-between gap-3 border-b border-base-300/50 p-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-secondary to-secondary/70 shadow-lg">
                            <MdAutoAwesome className="text-2xl text-white" />
                        </div>
                        <div>
                            <h2 className="font-bold text-base-content">Assistente do catálogo</h2>
                            <p className="text-xs text-base-content/60">
                                Busca itens, vincula parceiros e consulta o sistema antigo
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        {messages.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm gap-1"
                                onClick={() => handleReset()}
                                disabled={loading}
                                title="Esquecer o que foi conversado e começar do zero"
                            >
                                <MdRefresh className="text-lg" />
                                <span className="hidden sm:inline">Nova conversa</span>
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm btn-circle"
                            onClick={onClose}
                            aria-label="Fechar assistente"
                        >
                            <MdClose className="text-xl" />
                        </button>
                    </div>
                </header>

                <div className="flex-1 space-y-4 overflow-y-auto p-4">
                    {messages.length === 0 && (
                        <div className="space-y-3">
                            <p className="text-sm text-base-content/70">
                                Descreva o que precisa, como você falaria com um colega.
                            </p>
                            {SUGGESTIONS.map(suggestion => (
                                <button
                                    key={suggestion}
                                    type="button"
                                    className="btn btn-outline btn-sm h-auto w-full justify-start whitespace-normal py-2 text-left font-normal"
                                    onClick={() => handleSend(suggestion)}
                                >
                                    {suggestion}
                                </button>
                            ))}
                        </div>
                    )}

                    {messages.map((message, index) => (
                        <div
                            key={index}
                            className={
                                message.role === 'user'
                                    ? 'flex justify-end'
                                    : 'flex justify-start'
                            }
                        >
                            <div
                                className={`max-w-[85%] space-y-2 rounded-2xl px-4 py-2.5 text-sm ${
                                    message.role === 'user'
                                        ? 'bg-secondary text-secondary-content'
                                        : 'bg-base-200 text-base-content'
                                }`}
                            >
                                <p className="whitespace-pre-wrap">{message.content}</p>

                                {message.applied?.map(action => (
                                    <div
                                        key={action.logId}
                                        className="flex items-center justify-between gap-2 rounded-lg bg-base-100 px-3 py-2"
                                    >
                                        <span className="text-xs text-base-content/70">
                                            {action.description}
                                        </span>
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-xs gap-1"
                                            disabled={undoing === action.logId}
                                            onClick={() => handleUndo(action)}
                                        >
                                            <MdUndo className="text-base" />
                                            Desfazer
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    {loading && (
                        <div className="flex justify-start">
                            <div className="rounded-2xl bg-base-200 px-4 py-3">
                                <span className="loading loading-dots loading-sm" />
                            </div>
                        </div>
                    )}

                    <div ref={bottomRef} />
                </div>

                <footer className="border-t border-base-300/50 p-4">
                    <div className="flex items-end gap-2">
                        <textarea
                            ref={inputRef}
                            className="textarea textarea-bordered flex-1 resize-none"
                            rows={2}
                            placeholder="Ex.: adiciona TC de crânio ao Laboratório Vida"
                            value={input}
                            disabled={loading}
                            onChange={event => setInput(event.target.value)}
                            onKeyDown={event => {
                                if (event.key === 'Enter' && !event.shiftKey) {
                                    event.preventDefault()
                                    handleSend()
                                }
                            }}
                        />
                        <button
                            type="button"
                            className="btn btn-secondary btn-square"
                            disabled={loading || !input.trim()}
                            onClick={() => handleSend()}
                            aria-label="Enviar"
                        >
                            <MdSend className="text-xl" />
                        </button>
                    </div>
                    <p className="mt-2 text-xs text-base-content/50">
                        O assistente aplica as mudanças na hora. Use Desfazer se algo sair errado.
                    </p>
                </footer>
            </aside>
        </div>
    )
}
